import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import fetchAllPages from "../../utils/fetchAllPages";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

export const notificationAPI = createApi({
  reducerPath: "notificationAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetNotifications", "tagGetNotification", "tagGetMessages", "tagGetUsers"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET BANKS*/
    getBanks: builder.query({
      query: (body: any) => ({
        url: `/banks/${body.searchAndFilterString}`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
    //* GET BANKS BY ORGANIZATION*/
    getBanksByOrganization: builder.query({
      query: (body: any) => ({
        url: `/banks/org/${body.searchAndFilterString}`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
    //* GET BANK NOTIFICATIONS */
    getBankNotifications: builder.query({
      query: (body: any) => ({
        url: `/banks/notifications/${body.searchAndFilterString}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetNotifications"],
    }),
    //* *********************************************************** */
    //* GET BANK MESSAGES */
    getBankMessages: builder.query({
      query: (body: any) => ({
        url: `/banks/messages/${body.bankId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetMessages"],
    }),
    //* *********************************************************** */
    //* GET BANK NOTIFICATIONS ASSIGNED */
    getBankNotificationsAssigned: builder.query({
      query: (body: any) => ({
        url: `/banks/notifications-assignments/`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
    //* GET USERS BY ORGANIZATION */
    getUsersByOrganization: builder.query({
      query: (body: any) => ({
        url: `/users/${body.searchAndFilterString}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* GET NOTIFICATION */
    getNotification: builder.query({
      query: (body: any) => ({
        url: `/notifications/${body.idNotification}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetNotification"],
    }),
    //* *********************************************************** */
    //* GET NOTIFICATIONS BY ORGANIZATION */
    getNotificationsByOrganization: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(
          _baseQuery,
          `/notifications/?filter_target=organization_id&filter=${body.orgId}&page=1&limit=500`,
          { authorization: body.token },
        ),
      providesTags: ["tagGetNotifications"],
    }),
    //* *********************************************************** */
    //* GET NOTIFICATION ASSIGNMENTS BY ORGANIZATION */
    getNotificationAssignmentsByOrganization: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(
          _baseQuery,
          `/notifications/assign/?filter_target=organization_id&filter=${body.orgId}&page=1&limit=500`,
          { authorization: body.token },
        ),
      providesTags: ["tagGetNotifications"],
    }),
    //* *********************************************************** */
    //* POST ASSIGN NOTIFICATION TO A USER  */
    postAssignNotificationToUser: builder.mutation({
      query: (body: any) => ({
        url: `/notifications/assign/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* DELETE ASSIGN NOTIFICATION TO A USER  */
    deleteAssignNotificationToUser: builder.mutation({
      query: (body: any) => ({
        url: `/notifications/assign/${body.notificationId}/${body.userId}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* ASSIGN SCHEDULE TO USER  */
    putUser: builder.mutation({
      query: (body: any) => ({
        url: `/users/${body.id}`,
        method: "PUT",
        body: { id: body.id, ...body.body },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateUser" },
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* POST NOTIFICATION  */
    postNotification: builder.mutation({
      query: (body: any) => ({
        url: `/notifications/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateNotification" },
      invalidatesTags: ["tagGetNotifications"],
    }),
    //* *********************************************************** */
    //* PUT NOTIFICATION  */
    putNotification: builder.mutation({
      query: (body: any) => ({
        url: `/notifications/${body.id}`,
        method: "PUT",
        body: { id: body.id, ...body.body },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateNotification" },
      invalidatesTags: ["tagGetNotifications"],
    }),
    //* DELETE NOTIFICATION  */
    deleteNotification: builder.mutation({
      query: (body: any) => ({
        url: `/notifications/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetNotifications"],
    }),
  }),
});

// https://api.dev.agilegoods.com/api/users/?&page=1&limit=10

export const {
  useLazyGetBanksQuery,
  useLazyGetBanksByOrganizationQuery,
  useLazyGetBankNotificationsQuery,
  useLazyGetBankMessagesQuery,
  useLazyGetUsersByOrganizationQuery,
  useLazyGetBankNotificationsAssignedQuery,
  useLazyGetNotificationsByOrganizationQuery,
  useLazyGetNotificationAssignmentsByOrganizationQuery,
  usePostAssignNotificationToUserMutation,
  useDeleteAssignNotificationToUserMutation,
  usePostNotificationMutation,
  usePutNotificationMutation,
  useDeleteNotificationMutation,
  useLazyGetNotificationQuery,
  usePutUserMutation,
} = notificationAPI;
